import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'

const EDGE_MARGIN = 8
const MENU_GAP_PX = 4

export type ViewportMenuStyle = {
  top: number
  left: number
  minWidth: number
}

export function useViewportAnchoredMenu(
  open: boolean,
  onClose: () => void,
  options?: { minWidthPx?: number; alignEnd?: boolean },
) {
  const triggerRef = useRef<HTMLDivElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)
  const [menuStyle, setMenuStyle] = useState<ViewportMenuStyle | null>(null)
  const minWidthPx = options?.minWidthPx ?? 12 * 16
  const alignEnd = options?.alignEnd ?? true

  const updatePosition = useCallback(() => {
    const trigger = triggerRef.current
    if (!trigger) return
    const rect = trigger.getBoundingClientRect()
    const width = Math.max(rect.width, minWidthPx)
    let left = alignEnd ? rect.right - width : rect.left
    left = Math.max(EDGE_MARGIN, Math.min(left, window.innerWidth - width - EDGE_MARGIN))
    setMenuStyle({ top: rect.bottom + MENU_GAP_PX, left, minWidth: width })
  }, [alignEnd, minWidthPx])

  useLayoutEffect(() => {
    if (!open) {
      setMenuStyle(null)
      return
    }
    updatePosition()
  }, [open, updatePosition])

  useEffect(() => {
    if (!open) return

    const handleReposition = () => updatePosition()

    const handleScroll = (event: Event) => {
      const target = event.target
      if (target instanceof Node) {
        if (menuRef.current?.contains(target)) return
        if (triggerRef.current?.contains(target)) return
      }
      onClose()
    }

    window.addEventListener('resize', handleReposition)
    window.addEventListener('scroll', handleScroll, true)
    return () => {
      window.removeEventListener('resize', handleReposition)
      window.removeEventListener('scroll', handleScroll, true)
    }
  }, [open, onClose, updatePosition])

  return { triggerRef, menuRef, menuStyle }
}
